import React, { useEffect, useState } from "react";
import GetSplitPlanApi from "../../api/split-plans/GetSplitPlanApi";
import { SimpleLoader } from "../sub/Loaders";
import SplitPlanElement from "./SplitPlanElement";
import { Button } from "antd";

function SplitPlanSpace(props) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPlanId, setCurrentPlanId] = useState();
  const [startAt, setStartAt] = useState(0);
  const [totalChildCount, setTotalChildCount] = useState(0);
  const [isSplitedPlanDeleted, setIsSplitedPlanDeleted] = useState(false);
  const [isPlanSplited, setIsPlanSplited] = useState(false);
  const limit = 10;

  useEffect(() => {
    if (data.length === 0) {
      if (props.activeKeys.includes(props.planId)) {
        getPlans(props.planId, startAt);
        setCurrentPlanId(props.planId);
      }
    }
  }, [props.activeKeys]);

  useEffect(() => {
    if (props.splitedData) {
      if (!props.collapse) {
        const newData = data;
        newData.push(props.splitedData);
        setData(newData);
        setTotalChildCount(totalChildCount + 1);
        setIsPlanSplited(true);
        setStartAt(startAt + limit + 1);
      } else {
        getPlans(props.planId, startAt);
      }
    }
  }, [props.splitedData]);

  async function getPlans(id, num) {
    setLoading(true);
    const apiResponse = await GetSplitPlanApi(id, num, limit);
    if (apiResponse.success) {
      // Merge pages, de-duplicating by planId as a safety net so a plan can
      // never render twice even if a page offset overlaps a previous fetch.
      const merged = [...data, ...(apiResponse.data.plans || [])];
      const seen = new Set();
      const deduped = merged.filter((p) => {
        const key = p.planId ?? p.id;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      // Ensure displayed order respects orderIndex if provided
      const next = deduped.sort((a, b) => {
        const ai = typeof a.orderIndex === 'number' ? a.orderIndex : 999999;
        const bi = typeof b.orderIndex === 'number' ? b.orderIndex : 999999;
        if (ai !== bi) return ai - bi;
        const at = new Date(a.createdOn).getTime?.() || 0;
        const bt = new Date(b.createdOn).getTime?.() || 0;
        return at - bt;
      });
      setData(next);

      setTotalChildCount(apiResponse.data?.totalCount);
      setIsSplitedPlanDeleted(false);
      setIsPlanSplited(false);
    }
    setLoading(false);
  }

  function handleDelete(planId) {
    setIsSplitedPlanDeleted(true);
    setTotalChildCount(totalChildCount - 1);
    setStartAt(startAt + (limit - 1));

    const newData = [];
    data.forEach((element) => {
      if (planId !== element.planId) {
        newData.push(element);
      }
    });
    setData(newData);
  }
  function handleEdit(editedPlan) {
    const newData = [];
    data.forEach((element) => {
      if (editedPlan.planId === element.planId) {
        newData.push(editedPlan);
      } else {
        newData.push(element);
      }
    });
    setData(newData);
    
    // If cascade setting changed, trigger parent refresh
    if (props.onParentRefresh && typeof props.onParentRefresh === 'function') {
      props.onParentRefresh();
    }
  }

  return (
    <>
      {data?.map((element) => (
        <SplitPlanElement
          data={element}
          handleDelete={(planId) => handleDelete(planId)}
          handleEdit={(planId) => handleEdit(planId)}
          handleDeleteChange={(totalChildCount) =>
            props.handleDeleteChange(totalChildCount)
          }
          totalChildCount={totalChildCount}
          onTargetUpdate={(planId, target) => {
            // Update the specific plan's target achieved in the data
            const newData = data.map(plan => 
              plan.planId === planId 
                ? { ...plan, targetAchieved: target, performance: ((target / plan.target) * 100).toFixed(2) }
                : plan
            );
            setData(newData);
            
            // Notify parent if needed
            if (props.onParentRefresh) {
              props.onParentRefresh();
            }
          }}
        />
      ))}

      {loading ? (
        <SimpleLoader />
      ) : totalChildCount > data.length ? (
        <div className="flex items-center justify-center mt-2">
          <Button
            className="mb-2"
            onClick={() => {
              // Next page starts where the current data ends. Using a fixed
              // offset like `startAt + 2` while limit is 10 caused rows 2–9 to
              // be re-fetched and shown twice. Anchor the offset to how many
              // rows we've actually loaded so pages never overlap.
              const nextOffset = data.length;
              getPlans(currentPlanId, nextOffset);
              setStartAt(nextOffset);
            }}
          >
            Load more
          </Button>
        </div>
      ) : (
        ""
      )}
    </>
  );
}

export default SplitPlanSpace;
